import { Component, EventEmitter, Input, Output } from '@angular/core';
import { AbstractComponent } from '../../abstract.component';
import { TranslatePipe } from '../../../pipes/translate.pipe';
import { SolButtonComponent } from '../sol-button/sol-button.component';
import { SolIconComponent } from '../sol-icon/sol-icon.component';
import { SolIcon } from '@sol/icons';
import { SolButtonType } from '../../../models/core/sol-button';
import { SolText } from '../../../models/core/sol-text';
import { SolBanner, SolBannerAction, SolBannerMood } from '../../../models/core/sol-banner';

@Component({
  selector: 'sol-banner',
  imports: [SolButtonComponent, SolIconComponent, TranslatePipe],
  templateUrl: './sol-banner.component.html',
})
export class SolBannerComponent extends AbstractComponent<SolBanner> {
  // Static references
  protected readonly SolIcon = SolIcon;
  protected readonly SolButtonType = SolButtonType;

  // Computed
  protected readonly rendered$ = this.compute(state => state.rendered);
  protected readonly mood$ = this.compute(state => state.mood);
  protected readonly heading$ = this.compute(state => state.heading);
  protected readonly description$ = this.compute(state => state.description);
  protected readonly action$ = this.compute(state => state.action);
  protected readonly secondaryAction$ = this.compute(state => state.secondaryAction);
  protected readonly dismissible$ = this.compute(state => state.dismissible);
  protected readonly icon$ = this.compute(state => state.icon);
  protected readonly role$ = this.compute(state => state.role);
  protected readonly actionType$ = this.compute(state => state.actionType);
  protected readonly secondaryActionType$ = this.compute(state => state.secondaryActionType);
  protected readonly hasActions$ = this.compute(state => state.hasActions);
  protected readonly bannerClass$ = this.compute(state => state.hostClass);

  // Events
  @Output('action') private readonly actionEmitter = new EventEmitter<SolBannerAction>();
  @Output('secondaryAction') private readonly secondaryActionEmitter = new EventEmitter<SolBannerAction>();
  @Output() readonly visibleChange = new EventEmitter<boolean>();

  constructor() {
    super(SolBanner.of());
  }

  @Input()
  public set banner(banner: SolBanner) {
    this.patch(banner);
  }

  @Input()
  public set mood(mood: SolBannerMood) {
    this.update(state => state.withMood(mood));
  }

  @Input()
  public set heading(heading: SolText) {
    this.update(state => state.withHeading(heading));
  }

  @Input()
  public set description(description: SolText) {
    this.update(state => state.withDescription(description));
  }

  @Input()
  public set action(action: SolBannerAction | undefined) {
    this.update(state => state.withAction(action));
  }

  @Input()
  public set secondaryAction(secondaryAction: SolBannerAction | undefined) {
    this.update(state => state.withSecondaryAction(secondaryAction));
  }

  @Input()
  public set dismissible(dismissible: boolean) {
    this.update(state => state.withDismissible(dismissible));
  }

  @Input()
  public set visible(visible: boolean) {
    this.update(state => state.withRendered(visible));
  }

  protected onActionPress(action: SolBannerAction): void {
    this.actionEmitter.emit(action);
  }

  protected onSecondaryActionPress(action: SolBannerAction): void {
    this.secondaryActionEmitter.emit(action);
  }

  protected onDismissPress(): void {
    this.visibleChange.emit(false);
  }
}
